import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useState } from 'react';

interface CarouselOptions {
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  mediaItems?: Array<{
    url: string;
    kind: 'image' | 'video';
    videoPoster?: string | null;
  }>;
}

/**
 * Multi-image (carousel) state + overlay shared by every presentation shell,
 * so a carousel post shows the SAME nav (slide counter + ‹ › carets) on
 * each platform instead of Instagram-only. Returns the currently-shown image and
 * the overlay to drop into `MediaSlot`'s `overlay` slot. When there's 0–1 image
 * it's a no-op (overlay = undefined, currentImage = the single image).
 */
export function useCarousel(
  imageUrls: string[] | undefined,
  fallbackImage: string | null | undefined,
  options: CarouselOptions = {},
) {
  const sourceSlides =
    options.mediaItems ?? imageUrls?.map((url) => ({ url, kind: 'image' as const }));
  const slides = sourceSlides && sourceSlides.length > 1 ? sourceSlides : null;
  const [internalActive, setInternalActive] = useState(0);
  const total = slides?.length ?? 0;
  const rawActive = options.activeIndex ?? internalActive;
  const safeActive = slides ? Math.min(Math.max(0, rawActive), total - 1) : 0;
  const currentItem = sourceSlides?.[safeActive];
  const currentImage =
    currentItem?.kind === 'image' ? currentItem.url : (fallbackImage ?? undefined);
  const currentVideoUrl = currentItem?.kind === 'video' ? currentItem.url : undefined;
  const currentVideoPoster =
    currentItem?.kind === 'video' ? (currentItem.videoPoster ?? undefined) : undefined;

  const setActive = (next: number) => {
    const safeNext = slides ? Math.min(Math.max(0, next), total - 1) : 0;
    options.onActiveIndexChange?.(safeNext);
    if (options.activeIndex == null) setInternalActive(safeNext);
  };

  const carouselOverlay = slides ? (
    <>
      <span className="absolute right-2 top-2 rounded-full bg-background/80 px-2 py-0.5 text-micro font-medium tabular-nums text-foreground">
        {safeActive + 1}/{total}
      </span>
      {/* shadow-elevation-1: a translucent chip alone isn't reliable contrast —
          against a light or busy photo (verified live) it nearly vanishes,
          leaving a bare floating glyph. The shadow gives the chip edge
          definition regardless of what's behind it, the same reason every
          photo app (Photos, Instagram web itself) shadows overlay controls. */}
      {safeActive > 0 ? (
        <button
          type="button"
          aria-label="Previous slide"
          onClick={() => setActive(safeActive - 1)}
          className="absolute left-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground shadow-elevation-1 transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <CaretLeftIcon className="size-3.5" weight="bold" aria-hidden />
        </button>
      ) : null}
      {safeActive < total - 1 ? (
        <button
          type="button"
          aria-label="Next slide"
          onClick={() => setActive(safeActive + 1)}
          className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground shadow-elevation-1 transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <CaretRightIcon className="size-3.5" weight="bold" aria-hidden />
        </button>
      ) : null}
    </>
  ) : undefined;

  return {
    currentImage: currentItem?.kind === 'video' ? undefined : currentImage,
    currentVideoUrl,
    currentVideoPoster,
    currentItem,
    carouselOverlay,
    slides,
    safeActive,
    total,
  };
}
